'use client';

import { c as _c } from "react/compiler-runtime";
import { useWallet } from '@solana/wallet-adapter-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { type FC, type PropsWithChildren, type ReactElement, type ReactNode, useEffect, useRef } from 'react';
import { useIntercomJwt } from '@/entities/Auth/hooks/useIntercomJwt';
import { useIntercomManager } from '@/entities/Auth/hooks/useIntercomManager';
import { useRewardModal } from '@/entities/Global/hooks';
import { RewardModal } from '@/entities/Global/ui';
import { useLeaderboardSocket } from '@/entities/Leaderboard/hooks/useLeaderboardSocket';
import { getUserOptions } from '@/entities/User/api/getUser';
import { useUpdateSolanaWalletMutation } from '@/entities/User/api/updateSolanaWalletMutation';
import { useUserStore } from '@/entities/User/store/userStoreProvider';
import { VerificationStoreProvider } from '@/entities/User/store/verificationStoreProvider';
import { VerificationNotification } from '@/entities/User/ui/VerificationNotification';
import { FooterV2 } from '../../../components/organisms/FooterV2';
import { HeaderV2 } from '../../../components/organisms/HeaderV2';
import type { CelebrityDTO } from '../../../entities/Celebrity/types';
type Props = {
  celebrity?: CelebrityDTO;
  children: ReactNode;
  authClassName?: string;
};
const Layout: FC<PropsWithChildren<Props>> = t0 => {
  const $ = _c(32);
  const {
    children
  } = t0;
  const {
    reward,
    isOpenRewardModal,
    closeModal
  } = useRewardModal();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      enabled: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useLeaderboardSocket(t1);
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const setUser = useUserStore(_temp);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getUserOptions();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data
  } = useQuery(t2);
  const user = useUserStore(_temp2);
  const {
    data: jwtData,
    isLoading
  } = useIntercomJwt(!!user?.showIntercom);
  const wallet = useWallet();
  const {
    mutateAsync
  } = useUpdateSolanaWalletMutation();
  const prevAddress = useRef(user?.solanaAddress ?? null);
  let t3;
  if ($[2] !== isLoading || $[3] !== jwtData || $[4] !== pathname || $[5] !== user) {
    t3 = {
      user,
      jwtData,
      isLoading,
      pathname
    };
    $[2] = isLoading;
    $[3] = jwtData;
    $[4] = pathname;
    $[5] = user;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useIntercomManager(t3);
  let t4;
  if ($[7] !== mutateAsync || $[8] !== queryClient || $[9] !== setUser || $[10] !== user || $[11] !== wallet.publicKey) {
    t4 = () => {
      const solanaAddress = wallet.publicKey?.toString() ?? null;
      if ((user?.solanaAddress || null) === solanaAddress || prevAddress.current === solanaAddress) {
        return;
      }
      prevAddress.current = solanaAddress;
      mutateAsync({
        solanaAddress
      }).then(t5 => {
        const {
          solanaAddress: solanaAddress_0
        } = t5;
        if (!user) {
          return;
        }
        setUser({
          ...user,
          solanaAddress: solanaAddress_0 ?? undefined
        });
        queryClient.invalidateQueries({
          queryKey: ["user"]
        });
      });
    };
    $[7] = mutateAsync;
    $[8] = queryClient;
    $[9] = setUser;
    $[10] = user;
    $[11] = wallet.publicKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== user || $[14] !== wallet) {
    t5 = [user, wallet];
    $[13] = user;
    $[14] = wallet;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[16] !== data || $[17] !== setUser) {
    t6 = () => {
      if (data) {
        setUser(data);
      } else {
        setUser(null);
      }
    };
    t7 = [data, setUser];
    $[16] = data;
    $[17] = setUser;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <HeaderV2 onNavigate={_temp3} />;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== children) {
    t9 = <main className="grow pt-[40px] lg:pt-[107px]">{children}</main>;
    $[21] = children;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FooterV2 />;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== closeModal || $[25] !== isOpenRewardModal || $[26] !== reward) {
    t11 = <RewardModal reward={reward} isOpen={isOpenRewardModal} onClose={closeModal} />;
    $[24] = closeModal;
    $[25] = isOpenRewardModal;
    $[26] = reward;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <VerificationNotification />;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t9) {
    t13 = <VerificationStoreProvider>{t8}{t9}{t10}{t11}{t12}</VerificationStoreProvider>;
    $[29] = t11;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
export default Layout;
function _temp(store) {
  return store.setUser;
}
function _temp2(store_0) {
  return store_0.user;
}
function _temp3(path) {
  return console.warn("Navigate to:", path);
}